import { Dispatch, SetStateAction, memo, useMemo } from 'react';
import { Badge, Grid } from '@mantine/core';
import { MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import type { OrderHistoryDateField } from 'washday-sdk';
import Table from '../../../components/Table';
import { formatMoneyString } from '../../../utils/strings';
import {
  buildOrderHistoryReportColumnIds,
  formatOrderHistoryDate,
  formatHomeServiceIndicator,
  OrderHistoryReportRow
} from './reportRows';

const statusColor = (status: string) => {
  switch (status) {
    case 'requested':
    case 'pickup':
    case 'pickingup':
    case 'pending_store_reception_validation':
      return 'yellow';
    case 'cleaning':
      return 'blue';
    case 'ready':
    case 'delivery':
    case 'delivering':
      return 'cyan';
    case 'collected':
      return 'green';
    case 'cancelled':
    case 'uncollected':
      return 'red';
    default:
      return 'gray';
  }
};

export default memo(function TableView({
  tableInfoData,
  totalRowsCount,
  pages,
  isError,
  isLoading,
  pagination,
  setPagination,
  dateField
}: {
  tableInfoData: OrderHistoryReportRow[];
  totalRowsCount: number;
  pages: number;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  dateField: OrderHistoryDateField;
}) {
  const { t } = useTranslation(['FieldName', 'ReportsPage']);
  const columns = useMemo(
    () =>
      buildOrderHistoryReportColumnIds().map((columnId) => {
        switch (columnId) {
          case 'selectedDate':
            return {
              id: columnId,
              accessorFn: (row: OrderHistoryReportRow) =>
                formatOrderHistoryDate(row.selectedDate, row.storeTimezone, dateField),
              header: t('ReportsPage:ordersHistoryReport.headers.selectedDate')
            };
          case 'customerName':
          case 'storeName':
          case 'folio':
            return {
              id: columnId,
              accessorFn: (row: OrderHistoryReportRow) => row[columnId] || '-',
              header: t(`ReportsPage:ordersHistoryReport.headers.${columnId}`)
            };
          case 'status':
            return {
              id: columnId,
              accessorFn: (row: OrderHistoryReportRow) => row.status,
              header: t('ReportsPage:ordersHistoryReport.headers.status'),
              Cell: ({ cell }: { cell: { getValue: () => unknown } }) => {
                const value = (cell.getValue() as string) || '';
                return (
                  <Badge color={statusColor(value)} variant="light">
                    {t(`FieldName:orderStatuses.${value}`, { defaultValue: value })}
                  </Badge>
                );
              }
            };
          case 'pickup':
          case 'delivery':
            return {
              id: columnId,
              accessorFn: (row: OrderHistoryReportRow) =>
                formatHomeServiceIndicator(row[columnId], t('FieldName:yes')),
              header: t(`ReportsPage:ordersHistoryReport.headers.${columnId}`)
            };
          case 'total':
          case 'paid':
          case 'balance':
          default:
            return {
              id: columnId,
              accessorFn: (row: OrderHistoryReportRow) =>
                formatMoneyString((row[columnId] || 0).toFixed(2)),
              header: t(`ReportsPage:ordersHistoryReport.headers.${columnId}`)
            };
        }
      }),
    [dateField, t]
  );

  return (
    <Grid gutter="md">
      <Grid.Col span={12}>
        <Table
          tableColumns={columns}
          pagination={pagination}
          setPagination={setPagination}
          tableData={tableInfoData}
          isError={isError}
          paginationIsLoading={isLoading}
          numPages={pages}
          totalRowsCount={totalRowsCount}
        />
      </Grid.Col>
    </Grid>
  );
});
